/**
 * Single source of truth for which center-column overlay is open.
 *
 * The App used to track each modal with its own boolean (`gridSelectorVisible`,
 * `settingsVisible`, …) and recompute "is any modal open?" by OR-ing all of them
 * together in ~6 places that had to be kept in sync by hand. ModalManager replaces
 * those booleans: at most one modal is open at a time, so the open modal is a single
 * `ModalId | null`, and `inModal` derives from it instead of being maintained.
 *
 * The manager only tracks *which* modal is open; the App still owns showing each
 * panel. `closeAll()` hides the open one via the per-modal hide callbacks registered
 * at construction, so callers can dismiss whatever is open without naming it.
 */
export type ModalId =
    | 'gridSelector'
    | 'gridCreator'
    | 'gridDuplicator'
    | 'tutorialHub'
    | 'welcome'
    | 'settings'
    | 'leaderboard';

export class ModalManager {
    private active: ModalId | null = null;

    constructor(private readonly hiders: Record<ModalId, () => void>) {}

    /** Whether any center-column modal is currently open. */
    get inModal(): boolean {
        return this.active !== null;
    }

    /** The modal currently open, or null. */
    get current(): ModalId | null {
        return this.active;
    }

    /** Whether the given modal is the one currently open. */
    isOpen(id: ModalId): boolean {
        return this.active === id;
    }

    /** Record a modal as open. The caller is responsible for showing its panel. */
    open(id: ModalId): void {
        this.active = id;
    }

    /** Record the given modal as closed (no-op if it isn't the open one). */
    close(id: ModalId): void {
        if (this.active === id) this.active = null;
    }

    /** Hide whichever modal is open via its registered hider, then clear state. */
    closeAll(): void {
        if (this.active) {
            this.hiders[this.active]();
            this.active = null;
        }
    }
}
